import { useEffect, useState } from 'react';
import { createPortal } from 'react-dom';
import { Gift, RotateCcw, Trophy, UsersRound, X } from 'lucide-react';
import { api } from '@/services/api';

const EMPTY_STATE = {
  survey_id: null,
  config: { enabled: false, winners: 1, drawn: false, drawn_at: null, participants_count: null, winners_count: null },
  participants_count: 0,
  raffle: null,
  history: [],
};

const formatDateTime = (value) => {
  if (!value) return '';
  const date = new Date(value);
  return Number.isNaN(date.getTime())
    ? String(value).slice(0, 16)
    : new Intl.DateTimeFormat('es-AR', { dateStyle: 'medium', timeStyle: 'short' }).format(date);
};

const CARD_CLASS = 'admin-content-surface box-border w-full min-w-0 rounded-xl border border-[color:color-mix(in_srgb,var(--border)_50%,transparent)] bg-surface p-4 shadow-none dark:shadow-none';

function SurveyRaffleCard({ state, surveyId, theme = 'light', es = true, onUpdated }) {
  const [local, setLocal] = useState(state || EMPTY_STATE);
  const [enabled, setEnabled] = useState(!!state?.config?.enabled);
  const [winners, setWinners] = useState(String(state?.config?.winners ?? 1));
  const [savingConfig, setSavingConfig] = useState(false);
  const [running, setRunning] = useState(false);
  const [error, setError] = useState('');
  const [confirm, setConfirm] = useState(null); // null | { force: boolean }

  useEffect(() => {
    const next = state || EMPTY_STATE;
    setLocal(next);
    setEnabled(!!next.config?.enabled);
    setWinners(String(next.config?.winners ?? 1));
  }, [state]);

  const activeSurveyId = local.survey_id || surveyId;
  if (!activeSurveyId) return null;

  const copy = es
    ? {
        title: 'Sorteo',
        subtitle: 'Los participantes salen automáticamente de las respuestas de esta encuesta. Cada usuario participa una sola vez.',
        enable: 'Habilitar sorteo en esta encuesta',
        winners: 'Cantidad de ganadores',
        save: 'Guardar configuración',
        saving: 'Guardando...',
        participants: 'Participantes',
        configuredWinners: 'Ganadores configurados',
        draw: 'Realizar sorteo',
        redraw: 'Sortear de nuevo',
        drawing: 'Sorteando...',
        noParticipants: 'Todavía no hay participantes para sortear.',
        drawnAt: 'Sorteo realizado',
        winnersTitle: 'Ganadores',
        history: 'Sorteos anteriores',
        attempt: 'Sorteo',
        saved: 'Configuración guardada.',
        empty: 'El sorteo está deshabilitado. Activalo para poder realizarlo cuando la encuesta tenga respuestas.',
        confirmTitle: 'Confirmar sorteo',
        confirmDraw: 'Se van a sortear los participantes de esta encuesta y el resultado quedará guardado.',
        confirmReplaceTitle: 'Ya existe un sorteo',
        confirmReplace: 'Esta encuesta ya tiene un sorteo realizado. Si continuás, el resultado actual se reemplaza por uno nuevo y el anterior queda guardado en el historial.',
        cancel: 'Cancelar',
        confirmYes: 'Sortear',
        confirmReplaceYes: 'Sí, sortear de nuevo',
        errorGeneric: 'No se pudo completar la operación.',
        refreshHint: 'Para volver a sortear tenés que confirmar la acción.',
      }
    : {
        title: 'Raffle',
        subtitle: 'Participants come automatically from the responses to this survey. Each user takes part only once.',
        enable: 'Enable raffle on this survey',
        winners: 'Number of winners',
        save: 'Save settings',
        saving: 'Saving...',
        participants: 'Participants',
        configuredWinners: 'Configured winners',
        draw: 'Run raffle',
        redraw: 'Run raffle again',
        drawing: 'Drawing...',
        noParticipants: 'There are no participants to draw yet.',
        drawnAt: 'Raffle run on',
        winnersTitle: 'Winners',
        history: 'Previous raffles',
        attempt: 'Raffle',
        saved: 'Settings saved.',
        empty: 'The raffle is disabled. Enable it to run it once the survey has responses.',
        confirmTitle: 'Confirm raffle',
        confirmDraw: 'The participants of this survey will be drawn and the result will be saved.',
        confirmReplaceTitle: 'A raffle already exists',
        confirmReplace: 'This survey already has a raffle. If you continue, the current result is replaced by a new one and the previous one is kept in the history.',
        cancel: 'Cancel',
        confirmYes: 'Run raffle',
        confirmReplaceYes: 'Yes, run raffle again',
        errorGeneric: 'The operation could not be completed.',
        refreshHint: 'To run the raffle again you must confirm the action.',
      };

  const configuredWinners = Math.max(1, Math.min(100, Number(winners) || 1));
  const configDirty = enabled !== !!local.config?.enabled || configuredWinners !== (local.config?.winners ?? 1);
  const participantsCount = local.participants_count || 0;
  const raffle = local.raffle;
  const canDraw = enabled && participantsCount > 0;

  const saveConfig = async () => {
    setSavingConfig(true);
    setError('');
    try {
      await api.updateSurvey(activeSurveyId, { raffle_enabled: enabled, raffle_winners: configuredWinners });
      onUpdated?.();
    } catch (e) {
      setError(e?.response?.data?.detail || e?.message || copy.errorGeneric);
    } finally {
      setSavingConfig(false);
    }
  };

  const runDraw = async (force) => {
    setRunning(true);
    setError('');
    try {
      const data = await api.runSurveyRaffle(activeSurveyId, force);
      setLocal({ ...data, survey_id: data?.survey_id || activeSurveyId });
      setEnabled(!!data?.config?.enabled);
      setWinners(String(data?.config?.winners ?? configuredWinners));
      setConfirm(null);
      onUpdated?.();
    } catch (e) {
      const status = e?.response?.status;
      const detail = e?.response?.data?.detail;
      if (status === 409) {
        setConfirm({ force: true });
        setError(typeof detail === 'string' ? detail : '');
      } else {
        setError(typeof detail === 'string' ? detail : (e?.message || copy.errorGeneric));
        setConfirm(null);
      }
    } finally {
      setRunning(false);
    }
  };

  const primaryStyle = {
    backgroundColor: theme === 'light' ? '#000000' : '#FFFFFF',
    color: theme === 'light' ? '#FFFFFF' : '#000000',
  };

  return (
    <>
      <section className={`${CARD_CLASS} space-y-3`} data-testid="survey-raffle-card">
        <div className="flex min-w-0 flex-wrap items-start justify-between gap-2">
          <div className="flex min-w-0 items-center gap-2">
            <span className="flex h-8 w-8 shrink-0 items-center justify-center rounded-lg border border-[color:color-mix(in_srgb,#F5C451_35%,transparent)] bg-[color:color-mix(in_srgb,#F5C451_15%,transparent)] text-[#D97706]">
              <Gift className="h-4 w-4" />
            </span>
            <div className="min-w-0">
              <p className="text-sm font-semibold text-foreground">{copy.title}</p>
              <p className="max-w-2xl text-[11px] text-muted-foreground">{copy.subtitle}</p>
            </div>
          </div>
          <div className="flex shrink-0 flex-wrap items-center gap-2">
            <span className="inline-flex items-center gap-1 rounded-full bg-muted px-2.5 py-1 text-[11px] text-muted-foreground">
              <UsersRound className="h-3.5 w-3.5" />
              {copy.participants}: <b className="text-foreground">{participantsCount}</b>
            </span>
            {raffle ? (
              <span className="rounded-full border border-[color:color-mix(in_srgb,#22c55e_25%,transparent)] bg-[color:color-mix(in_srgb,#22c55e_12%,transparent)] px-2.5 py-1 text-[11px] font-medium text-[color:color-mix(in_srgb,#16a34a_80%,var(--fg))]">
                {copy.drawnAt}: {formatDateTime(raffle.drawn_at)}
              </span>
            ) : null}
          </div>
        </div>

        <div className="flex flex-wrap items-end gap-3">
          <label className="inline-flex items-center gap-2 text-xs text-foreground">
            <input
              type="checkbox"
              checked={enabled}
              onChange={(e) => setEnabled(e.target.checked)}
              className="h-4 w-4 shrink-0 accent-[#3A9CC8]"
              data-testid="survey-raffle-enabled"
            />
            {copy.enable}
          </label>
          <label className="text-xs text-muted-foreground">
            {copy.winners}
            <input
              type="number"
              min="1"
              max="100"
              value={winners}
              onChange={(e) => setWinners(e.target.value)}
              disabled={!enabled}
              className="ml-2 w-20 rounded-lg border border-[color:color-mix(in_srgb,var(--border)_50%,transparent)] bg-surface px-2 py-1 text-sm text-foreground outline-none focus:border-[#3A9CC8] disabled:opacity-60"
              data-testid="survey-raffle-winners"
            />
          </label>
          <button
            type="button"
            onClick={saveConfig}
            disabled={!configDirty || savingConfig}
            className="inline-flex h-8 items-center justify-center rounded-lg border border-[color:color-mix(in_srgb,var(--border)_50%,transparent)] bg-surface px-3 text-xs font-semibold text-foreground transition hover:bg-muted disabled:cursor-not-allowed disabled:opacity-50"
            data-testid="survey-raffle-save"
          >
            {savingConfig ? copy.saving : copy.save}
          </button>
          <button
            type="button"
            onClick={() => { setError(''); setConfirm({ force: !!raffle }); }}
            disabled={!canDraw || running}
            className="inline-flex h-8 items-center justify-center gap-2 rounded-lg px-3 text-xs font-semibold transition hover:opacity-90 disabled:cursor-not-allowed disabled:opacity-50"
            style={primaryStyle}
            data-testid="survey-raffle-run"
          >
            {raffle ? <RotateCcw className="h-3.5 w-3.5" /> : <Trophy className="h-3.5 w-3.5" />}
            {running ? copy.drawing : raffle ? copy.redraw : copy.draw}
          </button>
        </div>

        {!enabled ? <p className="text-[11px] text-muted-foreground">{copy.empty}</p> : null}
        {enabled && participantsCount === 0 ? <p className="text-[11px] text-muted-foreground">{copy.noParticipants}</p> : null}
        {error ? <p className="text-[11px] text-destructive">{error}</p> : null}

        {raffle ? (
          <div className="space-y-2 rounded-lg border border-[color:color-mix(in_srgb,var(--border)_45%,transparent)] bg-[color:color-mix(in_srgb,var(--fg)_3%,transparent)] p-3">
            <div className="flex flex-wrap items-center gap-2">
              <Trophy className="h-4 w-4 text-[#D97706]" />
              <p className="text-xs font-semibold uppercase tracking-wide text-muted-foreground">{copy.winnersTitle}</p>
              <span className="text-[11px] text-muted-foreground">
                {raffle.winners_count} / {raffle.participants_count} {copy.participants.toLowerCase()}
              </span>
              {local.history?.length > 1 ? (
                <span className="text-[11px] text-muted-foreground">· {copy.history}: {local.history.length}</span>
              ) : null}
            </div>
            <ul className="grid min-w-0 grid-cols-1 gap-2 sm:grid-cols-2 xl:grid-cols-3">
              {(raffle.winners || []).map((winner, index) => (
                <li key={`${winner.user_id}-${index}`} className="flex min-w-0 items-center gap-2 rounded-lg border border-[color:color-mix(in_srgb,var(--border)_45%,transparent)] bg-surface px-2 py-1.5">
                  <span className="flex h-6 w-6 shrink-0 items-center justify-center rounded-full bg-[color:color-mix(in_srgb,#F5C451_20%,transparent)] text-[11px] font-bold text-[#D97706]">{index + 1}</span>
                  <div className="min-w-0">
                    <p className="truncate text-sm font-medium text-foreground">{winner.name}</p>
                    <p className="truncate text-[11px] text-muted-foreground">
                      {[winner.house_name, winner.room_number ? `${es ? 'Hab.' : 'Room'} ${winner.room_number}` : null].filter(Boolean).join(' · ') || '—'}
                    </p>
                  </div>
                </li>
              ))}
            </ul>
            <p className="text-[11px] text-muted-foreground">{copy.refreshHint}</p>
          </div>
        ) : null}
      </section>

      {confirm ? createPortal(
        <div className="fixed inset-0 z-[100] flex items-center justify-center bg-black/50 p-4 backdrop-blur-sm" role="dialog" aria-modal="true">
          <div className="w-full max-w-md rounded-xl border border-[color:color-mix(in_srgb,var(--border)_60%,transparent)] bg-surface p-4 shadow-xl">
            <div className="flex items-start justify-between gap-3">
              <h3 className="text-base font-semibold text-foreground">{confirm.force ? copy.confirmReplaceTitle : copy.confirmTitle}</h3>
              <button type="button" onClick={() => setConfirm(null)} className="rounded-lg p-1 text-muted-foreground transition hover:bg-muted hover:text-foreground" aria-label={copy.cancel}>
                <X className="h-4 w-4" />
              </button>
            </div>
            <p className="mt-2 text-sm text-muted-foreground">{confirm.force ? copy.confirmReplace : copy.confirmDraw}</p>
            <div className="mt-3 grid grid-cols-1 gap-2 rounded-lg border border-[color:color-mix(in_srgb,var(--border)_45%,transparent)] bg-[color:color-mix(in_srgb,var(--fg)_3%,transparent)] p-2 text-xs text-muted-foreground sm:grid-cols-2">
              <p>{copy.participants}: <b className="text-foreground">{participantsCount}</b></p>
              <p>{copy.configuredWinners}: <b className="text-foreground">{Math.min(configuredWinners, participantsCount || configuredWinners)}</b></p>
            </div>
            {error ? <p className="mt-2 text-xs text-destructive">{error}</p> : null}
            <div className="mt-4 flex flex-col-reverse gap-2 sm:flex-row sm:justify-end">
              <button type="button" onClick={() => setConfirm(null)} className="inline-flex h-9 items-center justify-center rounded-lg border border-[color:color-mix(in_srgb,var(--border)_50%,transparent)] bg-surface px-3 text-sm font-medium text-foreground transition hover:bg-muted">
                {copy.cancel}
              </button>
              <button
                type="button"
                onClick={() => runDraw(confirm.force)}
                disabled={running}
                className="inline-flex h-9 items-center justify-center gap-2 rounded-lg px-3 text-sm font-semibold transition hover:opacity-90 disabled:cursor-not-allowed disabled:opacity-60"
                style={primaryStyle}
                data-testid="survey-raffle-confirm"
              >
                <Trophy className="h-4 w-4" />
                {running ? copy.drawing : confirm.force ? copy.confirmReplaceYes : copy.confirmYes}
              </button>
            </div>
          </div>
        </div>,
        document.body,
      ) : null}
    </>
  );
}

export default SurveyRaffleCard;
